import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, token } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Load cart when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      const fetchCart = async () => {
        setLoading(true);
        try {
          const cart = await api.getCart();
          setCartItems(cart.items);
          setCartCount(cart.item_count);
        } catch (error) {
          console.error('Failed to load cart:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchCart();
    } else {
      // Load from localStorage for guests
      const storedCart = localStorage.getItem('guest_cart');
      if (storedCart) {
        const items = JSON.parse(storedCart);
        setCartItems(items);
        setCartCount(items.reduce((sum, item) => sum + item.quantity, 0));
      } else {
        setCartItems([]);
        setCartCount(0);
      }
    }
  }, [isAuthenticated, token]);

  const updateLocalCart = useCallback((items) => {
    localStorage.setItem('guest_cart', JSON.stringify(items));
    setCartItems(items);
    setCartCount(items.reduce((sum, item) => sum + item.quantity, 0));
  }, []);

  const addToCart = useCallback(async (productId, quantity = 1, variantId = null) => {
    if (isAuthenticated) {
      try {
        const cart = await api.addToCart(productId, quantity, variantId);
        setCartItems(cart.items);
        setCartCount(cart.item_count);
        return true;
      } catch (error) {
        // If the session expired mid-flow, api.js already cleared the token +
        // fired auth:expired. Fall back to the guest cart so the click still
        // succeeds instead of showing "Failed to add to cart".
        if (error?.status === 401 || error?.status === 403) {
          const current = JSON.parse(localStorage.getItem('guest_cart') || '[]');
          const cartKey = variantId ? `${productId}_${variantId}` : productId;
          const existingIndex = current.findIndex(item => {
            const itemKey = item.variant_id ? `${item.product_id}_${item.variant_id}` : item.product_id;
            return itemKey === cartKey;
          });
          if (existingIndex >= 0) {
            current[existingIndex].quantity += quantity;
          } else {
            current.push({
              product_id: productId,
              quantity,
              variant_id: variantId,
              added_at: new Date().toISOString()
            });
          }
          updateLocalCart(current);
          return true;
        }
        throw error;
      }
    } else {
      // Guest cart in localStorage
      const current = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      // Create unique key for product+variant combination
      const cartKey = variantId ? `${productId}_${variantId}` : productId;
      const existingIndex = current.findIndex(item => {
        const itemKey = item.variant_id ? `${item.product_id}_${item.variant_id}` : item.product_id;
        return itemKey === cartKey;
      });
      
      if (existingIndex >= 0) {
        current[existingIndex].quantity += quantity;
      } else {
        current.push({ 
          product_id: productId, 
          quantity, 
          variant_id: variantId,
          added_at: new Date().toISOString() 
        });
      }
      
      updateLocalCart(current);
      return true;
    }
  }, [isAuthenticated, updateLocalCart]);

  const updateQuantity = useCallback(async (productId, quantity) => {
    if (quantity < 1) {
      return await removeFromCart(productId);
    }

    if (isAuthenticated) {
      try {
        const cart = await api.updateCartItem(productId, quantity);
        setCartItems(cart.items);
        setCartCount(cart.item_count);
        return true;
      } catch (error) {
        throw error;
      }
    } else {
      const current = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      const index = current.findIndex(item => item.product_id === productId);
      
      if (index >= 0) {
        current[index].quantity = quantity;
        updateLocalCart(current);
      }
      return true;
    }
  }, [isAuthenticated, updateLocalCart]);

  const removeFromCart = useCallback(async (productId) => {
    if (isAuthenticated) {
      try {
        const cart = await api.removeFromCart(productId);
        setCartItems(cart.items);
        setCartCount(cart.item_count);
        return true;
      } catch (error) {
        throw error;
      }
    } else {
      const current = JSON.parse(localStorage.getItem('guest_cart') || '[]');
      const updated = current.filter(item => item.product_id !== productId);
      updateLocalCart(updated);
      return true;
    }
  }, [isAuthenticated, updateLocalCart]);

  const clearCart = useCallback(async () => {
    if (isAuthenticated) {
      try {
        await api.clearCart();
        setCartItems([]);
        setCartCount(0);
        return true;
      } catch (error) {
        throw error;
      }
    } else {
      localStorage.removeItem('guest_cart');
      setCartItems([]);
      setCartCount(0);
      return true;
    }
  }, [isAuthenticated]);

  const getItemQuantity = useCallback((productId) => {
    const item = cartItems.find(i => i.product_id === productId);
    return item ? item.quantity : 0;
  }, [cartItems]);

  const isInCart = useCallback((productId) => {
    return cartItems.some(item => item.product_id === productId);
  }, [cartItems]);

  // Sync guest cart to server after login
  const syncGuestCart = useCallback(async () => {
    const guestCart = JSON.parse(localStorage.getItem('guest_cart') || '[]');
    if (guestCart.length > 0 && isAuthenticated) {
      try {
        await api.syncCart(guestCart);
        localStorage.removeItem('guest_cart');
        // Reload cart from server
        const cart = await api.getCart();
        setCartItems(cart.items);
        setCartCount(cart.item_count);
      } catch (error) {
        console.error('Failed to sync cart:', error);
      }
    }
  }, [isAuthenticated]);

  const value = {
    cartItems,
    cartCount,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getItemQuantity,
    isInCart,
    syncGuestCart
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
